import {
  GoogleMap,
  Polygon as GooglePolygon,
  Marker,
  InfoWindow,
  useJsApiLoader,
} from "@react-google-maps/api";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import turfArea from "@turf/area";
import turfDifference from "@turf/difference";
import turfIntersect from "@turf/intersect";
import { featureCollection, polygon as turfPolygon } from "@turf/helpers";
import {
  Button,
  Divider,
  Popconfirm,
  Segmented,
  Space,
  Spin,
  Switch,
  Tooltip,
} from "antd";
import {
  AimOutlined,
  BorderOutlined,
  DeleteOutlined,
  DragOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  NodeIndexOutlined,
  RedoOutlined,
  ThunderboltOutlined,
  UndoOutlined,
} from "@ant-design/icons";
import {
  TerraDraw,
  TerraDrawPolygonMode,
  TerraDrawSelectMode,
  TerraDrawModeUndoRedo,
  TerraDrawUndoRedoKeyboardShortcuts,
} from "terra-draw";
import { TerraDrawGoogleMapsAdapter } from "terra-draw-google-maps-adapter";
import { ICoordinates } from "../../../../models/area/request";
import {
  formatPolygonArea,
  validatePolygonCoordinates,
} from "../../../../utils/helpers/polygon-validation";
import {
  PolygonIssue,
  autoRepairSelfIntersection,
  detectPolygonIssues,
  findSpikes,
  removeDuplicatePoints,
  removeVertex,
  toOpenRing,
} from "../../../../utils/helpers/polygon-issues";
import { calculateRouteDistance } from "../../../../utils/geo/haversine";
import {
  MAP_DEFAULT_LOCATION,
  GOOGLE_MAPS_LIBRARIES,
} from "../../../../utils/helpers/constants";
import { IResponseTracking } from "../../../../models/map/response";
import { IStore } from "../../../../models/store/response";
import { getImageByType } from "../../../../models/map/enum";
import { getStorWorkingStatus } from "../../../../models/store/enum";
import { useTranslation } from "react-i18next";
import styles from "./style.module.scss";

const containerStyle = {
  width: "100%",
  height: "60vh",
};

interface Center {
  lat: number;
  lng: number;
}

interface IProps {
  points?: ICoordinates;
  allPoints?: any;
  serviceAreaPoints?: any;
  onGeometryIssuesChange?: (issues: {
    outsideServiceArea: number;
    currentNeighborOverlapArea: number;
    neighborNeighborOverlapArea: number;
    polygonIssueCount: number;
  }) => void;
  currentAreaId?: number | string;
  editable: boolean;
  draggable: boolean;
  showDefault?: boolean;
  onChange?: (e: any) => void;

  location?: IResponseTracking[];
  selectedDriver?: IResponseTracking;
  setSelectedDriver?: (val: IResponseTracking) => void;
  stores?: IStore[];
  mapWidth?: string;
  mapHeight?: string;
}

interface NeighborRing {
  key: string;
  index: number;
  ring: number[][];
  path: Center[];
}

interface OverlapIssue {
  key: string;
  kind: "current_neighbor" | "neighbor_neighbor";
  area: number;
  points: number[][];
  paths: Center[][];
}

type HistoryCoordinates = number[][][] | null;

// Terra Draw replaces Google's deprecated Drawing Library (removed May 2026).
// It draws over the existing Google Map via TerraDrawGoogleMapsAdapter and gives
// us professional editing: snapping onto neighbouring borders, dragging /
// inserting / deleting individual vertices, dragging the whole polygon,
// undo/redo, and live problem detection with one-click fixes.
//
// The shared GOOGLE_MAPS_LIBRARIES constant keeps the useJsApiLoader `libraries`
// array identical across every map component (the single global Loader requires
// a match — Terra Draw itself does not need any extra library).

const POLYGON_MODE = "polygon";
const SELECT_MODE = "select";

const VALID_COLOR = "#1677ff";
const INVALID_COLOR = "#d4380d";
const NEIGHBOR_COLOR = "#fa8c16";
const SERVICE_AREA_COLOR = "#08979c";
const MIDPOINT_COLOR = "#52c41a";
const CURRENT_OVERLAP_COLOR = "#cf1322";
const NEIGHBOR_OVERLAP_COLOR = "#d46b08";
const OUTSIDE_SERVICE_COLOR = "#a8071a";

// Pixel radius within which the cursor snaps onto a neighbouring border.
const SNAP_PIXEL_DISTANCE = 34;
// We still clip tiny slivers, but do not keep alarming the user for a few square
// meters created by floating-point polygon operations at a nearly tangent edge.
const CLIP_OVERLAP_AREA_EPSILON_M2 = 0.05;
const VISIBLE_OVERLAP_AREA_EPSILON_M2 = 5;

// Order + label keys for grouping detected polygon problems in the panel, so a
// long list collapses into a few clear rows ("Crossings: 3" not 3 messages).
const ISSUE_TYPE_ORDER = [
  "self_intersection",
  "duplicate",
  "spike",
  "min_points",
  "zero_area",
] as const;
const ISSUE_LABEL_KEYS: Record<string, string> = {
  self_intersection: "map_issue_self_intersection",
  duplicate: "map_issue_duplicate",
  spike: "map_issue_spike",
  min_points: "polygon_min_points",
  zero_area: "polygon_zero_area",
};

const round6 = (n: number) => Number(Number(n).toFixed(6));

// Human-readable perimeter, e.g. "850 m" or "4.32 km".
const formatDistance = (km: number): string => {
  if (!km || km <= 0) return "0 m";
  if (km < 1) return `${Math.round(km * 1000).toLocaleString()} m`;
  return `${km.toLocaleString(undefined, { maximumFractionDigits: 2 })} km`;
};

const roundedRing = (ring?: number[][] | null): number[][] | null =>
  Array.isArray(ring) ? ring.map((c) => [round6(c[0]), round6(c[1])]) : null;

// Stable identity for a ring (rounded JSON) used to recognise our own onChange
// echoes; `null` when there is no valid ring.
const ringKey = (ring?: number[][] | null): string | null =>
  Array.isArray(ring) && ring.length >= 3
    ? JSON.stringify(roundedRing(ring))
    : null;

const historyCoordinatesKey = (coordinates: HistoryCoordinates): string =>
  ringKey(coordinates?.[0] ?? null) ?? "empty";

const cloneHistoryCoordinates = (
  coordinates: HistoryCoordinates,
): HistoryCoordinates => {
  const ring = coordinates?.[0];
  if (!Array.isArray(ring) || ring.length < 3) return null;
  return [
    ensureClosed(ring).map((c) => [round6(c[0]), round6(c[1])]),
  ];
};

const areaItemId = (item: any): number | string | undefined => item?.id;

const areaItemRing = (item: any): number[][] | null => {
  const source = item?.points ?? item;
  const ring = source?.coordinates?.[0];
  return Array.isArray(ring) ? ring : null;
};

// GeoJSON rings must be closed (first === last); make it so without mutating.
const ensureClosed = (ring: number[][]): number[][] => {
  if (!ring.length) return ring;
  const first = ring[0];
  const last = ring[ring.length - 1];
  return first[0] === last[0] && first[1] === last[1]
    ? ring
    : [...ring, [first[0], first[1]]];
};

const coordinateKey = (point: number[]) =>
  `${round6(point[0]).toFixed(6)},${round6(point[1]).toFixed(6)}`;

const uniqueCoordinates = (points: number[][]): number[][] => {
  const seen = new Set<string>();
  const out: number[][] = [];
  points.forEach((point) => {
    const key = coordinateKey(point);
    if (!seen.has(key)) {
      seen.add(key);
      out.push([round6(point[0]), round6(point[1])]);
    }
  });
  return out;
};

const toMapPath = (ring: number[][]): Center[] =>
  toOpenRing(ring).map((point) => ({ lng: point[0], lat: point[1] }));

const pointOnSegment = (point: number[], start: number[], end: number[]) => {
  const cross =
    (point[1] - start[1]) * (end[0] - start[0]) -
    (point[0] - start[0]) * (end[1] - start[1]);
  if (Math.abs(cross) > 1e-10) return false;
  const dot =
    (point[0] - start[0]) * (end[0] - start[0]) +
    (point[1] - start[1]) * (end[1] - start[1]);
  if (dot < 0) return false;
  const lenSq =
    (end[0] - start[0]) * (end[0] - start[0]) +
    (end[1] - start[1]) * (end[1] - start[1]);
  return dot <= lenSq;
};

const pointInRing = (point: number[], ring: number[][]): boolean => {
  const open = toOpenRing(ring);
  if (open.length < 3) return false;

  for (let i = 0; i < open.length; i++) {
    if (pointOnSegment(point, open[i], open[(i + 1) % open.length])) {
      return false;
    }
  }

  let inside = false;
  for (let i = 0, j = open.length - 1; i < open.length; j = i++) {
    const xi = open[i][0];
    const yi = open[i][1];
    const xj = open[j][0];
    const yj = open[j][1];
    const intersects =
      yi > point[1] !== yj > point[1] &&
      point[0] < ((xj - xi) * (point[1] - yi)) / (yj - yi) + xi;
    if (intersects) inside = !inside;
  }
  return inside;
};

const properSegmentIntersection = (
  a1: number[],
  a2: number[],
  b1: number[],
  b2: number[],
): number[] | null => {
  const dxA = a2[0] - a1[0];
  const dyA = a2[1] - a1[1];
  const dxB = b2[0] - b1[0];
  const dyB = b2[1] - b1[1];
  const denom = dxA * dyB - dyA * dxB;
  if (Math.abs(denom) <= 1e-12) return null;

  const dx = b1[0] - a1[0];
  const dy = b1[1] - a1[1];
  const ua = (dx * dyB - dy * dxB) / denom;
  const ub = (dx * dyA - dy * dxA) / denom;
  if (ua <= 1e-9 || ua >= 1 - 1e-9 || ub <= 1e-9 || ub >= 1 - 1e-9) {
    return null;
  }
  return [a1[0] + ua * dxA, a1[1] + ua * dyA];
};

const overlapPointsBetweenRings = (
  firstRing: number[][],
  secondRing: number[][],
): number[][] => {
  const first = toOpenRing(firstRing);
  const second = toOpenRing(secondRing);
  if (first.length < 3 || second.length < 3) return [];

  const points: number[][] = [];
  for (let i = 0; i < first.length; i++) {
    for (let j = 0; j < second.length; j++) {
      const intersection = properSegmentIntersection(
        first[i],
        first[(i + 1) % first.length],
        second[j],
        second[(j + 1) % second.length],
      );
      if (intersection) points.push(intersection);
    }
  }

  const firstInside = first.find((point) => pointInRing(point, second));
  if (firstInside) points.push(firstInside);
  const secondInside = second.find((point) => pointInRing(point, first));
  if (secondInside) points.push(secondInside);

  return uniqueCoordinates(points);
};

const turfPolygonFromRing = (ring?: number[][] | null) => {
  const open = toOpenRing(ring ?? []);
  if (open.length < 3) return null;
  const closed = ensureClosed(open.map((c) => [round6(c[0]), round6(c[1])]));
  try {
    return turfPolygon([closed]);
  } catch {
    return null;
  }
};

const turfFeatureArea = (feature: any): number => {
  if (!feature) return 0;
  try {
    return turfArea(feature);
  } catch {
    return 0;
  }
};

const featureToOuterRings = (feature: any): number[][][] => {
  const geometry = feature?.geometry;
  const coordinates = geometry?.coordinates;
  if (!geometry || !Array.isArray(coordinates)) return [];

  if (geometry.type === "Polygon") {
    const outerRing = coordinates[0];
    return Array.isArray(outerRing) && outerRing.length >= 4 ? [outerRing] : [];
  }

  if (geometry.type === "MultiPolygon") {
    return coordinates
      .map((polygonRings: any) => polygonRings?.[0])
      .filter((outerRing: any) => Array.isArray(outerRing) && outerRing.length >= 4);
  }

  return [];
};

const featureToMapPaths = (feature: any): Center[][] =>
  featureToOuterRings(feature)
    .map((ring) => toMapPath(toOpenRing(ring)))
    .filter((path) => path.length >= 3);

const largestOuterRingFromFeature = (feature: any): number[][] | null => {
  let largestRing: number[][] | null = null;
  let largestArea = 0;

  featureToOuterRings(feature).forEach((ring) => {
    const closed = ensureClosed(
      toOpenRing(ring).map((c) => [round6(c[0]), round6(c[1])]),
    );
    if (closed.length < 4) return;

    try {
      const area = turfArea(turfPolygon([closed]));
      if (area > largestArea) {
        largestArea = area;
        largestRing = closed;
      }
    } catch {
      /* noop */
    }
  });

  return largestRing;
};

const overlapFeatureBetweenRings = (
  firstRing: number[][],
  secondRing: number[][],
  minimumAreaM2 = VISIBLE_OVERLAP_AREA_EPSILON_M2,
): { feature: any; area: number; paths: Center[][] } | null => {
  const firstFeature = turfPolygonFromRing(firstRing);
  const secondFeature = turfPolygonFromRing(secondRing);
  if (!firstFeature || !secondFeature) return null;

  try {
    const feature = turfIntersect(
      featureCollection([firstFeature, secondFeature] as any) as any,
    );
    const area = turfFeatureArea(feature);
    const paths = featureToMapPaths(feature);
    if (area <= minimumAreaM2 || !paths.length) return null;
    return { feature, area, paths };
  } catch {
    return null;
  }
};

const distanceMeters = (a: number[], b: number[]): number => {
  const R = 6371000;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(b[1] - a[1]);
  const dLng = toRad(b[0] - a[0]);
  const lat = toRad((a[1] + b[1]) / 2);
  const x = dLng * Math.cos(lat);
  return Math.sqrt(x * x + dLat * dLat) * R;
};

const nearestOnCoordinateSegment = (
  point: number[],
  start: number[],
  end: number[],
): number[] => {
  const lat = ((point[1] + start[1] + end[1]) / 3) * (Math.PI / 180);
  const xScale = Math.cos(lat);
  const px = point[0] * xScale;
  const py = point[1];
  const ax = start[0] * xScale;
  const ay = start[1];
  const bx = end[0] * xScale;
  const by = end[1];
  const dx = bx - ax;
  const dy = by - ay;
  const len2 = dx * dx + dy * dy;
  if (len2 === 0) return start;
  const t = Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / len2));
  return [start[0] + (end[0] - start[0]) * t, start[1] + (end[1] - start[1]) * t];
};

const snapRingToNeighborBorders = (
  ring: number[][],
  neighborRings: number[][][],
  toleranceMeters = 0.25,
): number[][] =>
  toOpenRing(ring).map((point) => {
    let bestPoint: number[] | null = null;
    let bestDistance = toleranceMeters;

    neighborRings.forEach((neighborRing) => {
      const neighbor = toOpenRing(neighborRing);
      for (let i = 0; i < neighbor.length; i++) {
        const candidate = nearestOnCoordinateSegment(
          point,
          neighbor[i],
          neighbor[(i + 1) % neighbor.length],
        );
        const distance = distanceMeters(point, candidate);
        if (distance <= bestDistance) {
          bestDistance = distance;
          bestPoint = candidate;
        }
      }
    });

    return bestPoint ? [round6(bestPoint[0]), round6(bestPoint[1])] : point;
  });

const cleanRepairedRing = (ring: number[][]): number[][] => {
  let cleaned = removeDuplicatePoints(ring);
  cleaned = autoRepairSelfIntersection(cleaned);
  cleaned = removeDuplicatePoints(cleaned);

  for (let guard = 0; guard < cleaned.length; guard++) {
    const spikes = findSpikes(cleaned);
    if (!spikes.length) break;
    let next = cleaned;
    spikes
      .sort((a, b) => b - a)
      .forEach((index) => {
        next = removeVertex(next, index);
      });
    if (next.length < 3 || next.length === cleaned.length) break;
    cleaned = removeDuplicatePoints(next);
  }

  return cleaned;
};

const outsideContainingAreaFeature = (
  currentRing: number[][],
  containingRings: number[][][],
  minimumAreaM2 = VISIBLE_OVERLAP_AREA_EPSILON_M2,
): { feature: any; area: number; paths: Center[][] } | null => {
  const currentFeature = turfPolygonFromRing(currentRing);
  if (!currentFeature || !containingRings.length) return null;

  const containingFeatures = containingRings
    .map((ring) => turfPolygonFromRing(ring))
    .filter(Boolean);
  if (!containingFeatures.length) return null;

  try {
    const outsideFeature = turfDifference(
      featureCollection([currentFeature, ...containingFeatures] as any) as any,
    );
    const area = turfFeatureArea(outsideFeature);
    const paths = featureToMapPaths(outsideFeature);
    if (area <= minimumAreaM2 || !paths.length) return null;
    return { feature: outsideFeature, area, paths };
  } catch {
    return null;
  }
};

const trimRingToContainingAreas = (
  currentRing: number[][],
  containingRings: number[][][],
): number[][] | null => {
  const currentFeature = turfPolygonFromRing(currentRing);
  if (!currentFeature || !containingRings.length) return null;

  const outside = outsideContainingAreaFeature(
    currentRing,
    containingRings,
    CLIP_OVERLAP_AREA_EPSILON_M2,
  );
  if (!outside) return null;

  try {
    const trimmedFeature = turfDifference(
      featureCollection([currentFeature, outside.feature] as any) as any,
    );
    const trimmedRing = largestOuterRingFromFeature(trimmedFeature);
    if (!trimmedRing) return null;
    const openRing = cleanRepairedRing(
      snapRingToNeighborBorders(toOpenRing(trimmedRing), containingRings),
    );
    return openRing.length >= 3 ? openRing : null;
  } catch {
    return null;
  }
};

const overlappingNeighborFeaturesForRing = (
  ring: number[][],
  neighbors: number[][][],
): any[] =>
  neighbors
    .map((neighborRing) => ({
      feature: turfPolygonFromRing(neighborRing),
      overlap: overlapFeatureBetweenRings(
        ring,
        neighborRing,
        CLIP_OVERLAP_AREA_EPSILON_M2,
      ),
    }))
    .filter((item) => item.feature && item.overlap)
    .map((item) => item.feature);

const subtractNeighborOverlapsOnce = (
  currentRing: number[][],
  neighbors: number[][][],
): number[][] | null => {
  const currentFeature = turfPolygonFromRing(currentRing);
  if (!currentFeature) return null;

  const overlappingNeighbors = overlappingNeighborFeaturesForRing(
    currentRing,
    neighbors,
  );
  if (!overlappingNeighbors.length) return null;

  try {
    const trimmedFeature = turfDifference(
      featureCollection([currentFeature, ...overlappingNeighbors] as any) as any,
    );
    const trimmedRing = largestOuterRingFromFeature(trimmedFeature);
    if (!trimmedRing) return null;
    const openRing = toOpenRing(trimmedRing);
    return openRing.length >= 3 ? openRing : null;
  } catch {
    return null;
  }
};

const subtractNeighborOverlaps = (
  currentRing: number[][],
  neighbors: number[][][],
): number[][] | null => {
  let openRing = toOpenRing(currentRing);
  let changed = false;

  for (let guard = 0; guard < 4; guard++) {
    const clippedRing = subtractNeighborOverlapsOnce(openRing, neighbors);
    if (!clippedRing) break;
    openRing = cleanRepairedRing(
      snapRingToNeighborBorders(clippedRing, neighbors),
    );
    changed = true;
    if (openRing.length < 3) return null;
  }

  return changed && openRing.length >= 3 ? openRing : null;
};

// Nearest point on segment AB to point P, all in screen-pixel space.
const nearestOnSegment = (
  px: number,
  py: number,
  ax: number,
  ay: number,
  bx: number,
  by: number,
): { x: number; y: number } => {
  const dx = bx - ax;
  const dy = by - ay;
  const len2 = dx * dx + dy * dy;
  if (len2 === 0) return { x: ax, y: ay };
  let t = ((px - ax) * dx + (py - ay) * dy) / len2;
  t = Math.max(0, Math.min(1, t));
  return { x: ax + t * dx, y: ay + t * dy };
};

/**
 * Renders React UI into a Google Maps custom control so it lives INSIDE the map
 * container — and therefore stays visible when the map is in fullscreen (a plain
 * sibling DIV below the map would vanish there).
 */
const MapControlPortal: React.FC<{
  map: google.maps.Map;
  position: number;
  children: React.ReactNode;
}> = ({ map, position, children }) => {
  const [node] = useState(() => document.createElement("div"));
  useEffect(() => {
    if (!map) return;
    const controls = map.controls[position];
    controls.push(node);
    return () => {
      for (let i = 0; i < controls.getLength(); i++) {
        if (controls.getAt(i) === node) {
          controls.removeAt(i);
          break;
        }
      }
    };
  }, [map, position, node]);
  return createPortal(children, node);
};

const CombinedMap = ({
  onChange,
  points,
  showDefault,
  allPoints,
  serviceAreaPoints,
  onGeometryIssuesChange,
  currentAreaId,
  editable,
  location,
  mapWidth,
  mapHeight,
  stores,
}: IProps) => {
  const { t } = useTranslation();

  // Drawing/editing is on unless this is an explicit view-only map.
  const interactive = !showDefault && editable !== false;

  const [mapInstance, setMapInstance] = useState<google.maps.Map | null>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const drawRef = useRef<TerraDraw | null>(null);
  const userFeatureIdRef = useRef<string | number | null>(null);
  const snapReferenceRingsRef = useRef<NeighborRing[]>([]);
  const historyRef = useRef<HistoryCoordinates[]>([null]);
  const historyIndexRef = useRef(0);
  const mapKeyboardActiveRef = useRef(false);
  const mapPointerCleanupRef = useRef<(() => void) | null>(null);
  const moveAreaEnabledRef = useRef(false);
  const drawReadyRef = useRef(false);
  const pointsRef = useRef(points);
  // Guards against reacting to our own programmatic store writes.
  const suppressRef = useRef(false);
  const onChangeRef = useRef(onChange);
  const onGeometryIssuesChangeRef = useRef(onGeometryIssuesChange);
  const snapEnabledRef = useRef(true);
  const didCenterRef = useRef(false);
  // The ring (rounded JSON) currently agreed between the store and the form, so
  // we ignore onChange echoes and never rebuild the feature mid-edit.
  const syncedRingKeyRef = useRef<string | null>(null);

  const [activeTool, setActiveTool] = useState<"draw" | "edit">("draw");
  const [snapEnabled, setSnapEnabled] = useState(true);
  const [moveAreaEnabled, setMoveAreaEnabled] = useState(false);
  const [currentCoords, setCurrentCoords] = useState<number[][][] | null>(null);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);

  const [selectedMarker, setSelectedMarker] =
    useState<IResponseTracking | null>(null);
  const [selectedStoreMarker, setSelectedStoreMarker] =
    useState<IStore | null>(null);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    onGeometryIssuesChangeRef.current = onGeometryIssuesChange;
  }, [onGeometryIssuesChange]);

  useEffect(() => {
    pointsRef.current = points;
  }, [points]);

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: `${process.env.REACT_APP_GOOGLE_KEY}`,
    libraries: GOOGLE_MAPS_LIBRARIES,
  });

  // Center once; the map is never recentred while editing (avoids jumping).
  const [center] = useState<Center>(() => {
    const ring = points?.coordinates?.[0];
    if (Array.isArray(ring) && ring.length) {
      return { lat: ring[0][1], lng: ring[0][0] };
    }
    return MAP_DEFAULT_LOCATION;
  });

  // The original ring, captured once, so the area being edited is excluded from
  // the neighbour reference layer even after the user reshapes it.
  const [originalRingKey] = useState<string | null>(() =>
    ringKey(points?.coordinates?.[0] ?? null),
  );

  const neighborRings = useMemo<NeighborRing[]>(
    () =>
      (allPoints ?? [])
        .map((el: any, index: number) => {
          const itemId = areaItemId(el);
          if (
            currentAreaId != null &&
            itemId != null &&
            String(itemId) === String(currentAreaId)
          ) {
            return null;
          }
          const ring = areaItemRing(el);
          if (!Array.isArray(ring) || ring.length < 3) return null;
          if (ringKey(ring) === originalRingKey) return null;
          const closed = ensureClosed(ring);
          return {
            key: `neighbor-${index}`,
            index,
            ring: closed,
            path: toMapPath(closed),
          };
        })
        .filter(Boolean) as NeighborRing[],
    [allPoints, currentAreaId, originalRingKey],
  );

  const serviceAreaRings = useMemo<NeighborRing[]>(
    () =>
      (serviceAreaPoints ?? [])
        .map((el: any, index: number) => {
          const ring = areaItemRing(el);
          if (!Array.isArray(ring) || ring.length < 3) return null;
          const closed = ensureClosed(ring);
          return {
            key: `service-area-${areaItemId(el) ?? index}`,
            index,
            ring: closed,
            path: toMapPath(closed),
          };
        })
        .filter(Boolean) as NeighborRing[],
    [serviceAreaPoints],
  );

  useEffect(() => {
    snapReferenceRingsRef.current = [...neighborRings, ...serviceAreaRings];
  }, [neighborRings, serviceAreaRings]);

  const currentNeighborOverlaps = useMemo<OverlapIssue[]>(() => {
    const currentRing = currentCoords?.[0];
    if (!currentRing) return [];
    return neighborRings
      .map((neighbor) => {
        const overlap = overlapFeatureBetweenRings(currentRing, neighbor.ring);
        if (!overlap) return null;
        return {
          key: `current-${neighbor.key}`,
          kind: "current_neighbor" as const,
          area: overlap.area,
          points: overlapPointsBetweenRings(currentRing, neighbor.ring),
          paths: overlap.paths,
        };
      })
      .filter(Boolean) as OverlapIssue[];
  }, [currentCoords, neighborRings]);

  const neighborNeighborOverlaps = useMemo<OverlapIssue[]>(() => {
    const overlaps: OverlapIssue[] = [];
    for (let i = 0; i < neighborRings.length; i++) {
      for (let j = i + 1; j < neighborRings.length; j++) {
        const overlap = overlapFeatureBetweenRings(
          neighborRings[i].ring,
          neighborRings[j].ring,
        );
        if (!overlap) continue;
        overlaps.push({
          key: `${neighborRings[i].key}-${neighborRings[j].key}`,
          kind: "neighbor_neighbor",
          area: overlap.area,
          points: overlapPointsBetweenRings(
            neighborRings[i].ring,
            neighborRings[j].ring,
          ),
          paths: overlap.paths,
        });
      }
    }
    return overlaps;
  }, [neighborRings]);

  const overlapMarkers = useMemo(
    () =>
      [...currentNeighborOverlaps, ...neighborNeighborOverlaps].flatMap(
        (issue) =>
          issue.points.map((point, index) => ({
            key: `${issue.key}-${index}`,
            kind: issue.kind,
            position: { lng: point[0], lat: point[1] },
          })),
      ),
    [currentNeighborOverlaps, neighborNeighborOverlaps],
  );

  const totalCurrentNeighborOverlapArea = useMemo(
    () => currentNeighborOverlaps.reduce((sum, item) => sum + item.area, 0),
    [currentNeighborOverlaps],
  );

  const totalNeighborNeighborOverlapArea = useMemo(
    () => neighborNeighborOverlaps.reduce((sum, item) => sum + item.area, 0),
    [neighborNeighborOverlaps],
  );

  const totalOverlapCount =
    currentNeighborOverlaps.length + neighborNeighborOverlaps.length;
  const hasServiceAreaLayer = serviceAreaRings.length > 0;
  const neighborAreasLabel = hasServiceAreaLayer
    ? t("map_discount_code_areas")
    : t("map_neighbor_areas");
  const currentNeighborOverlapLabel = hasServiceAreaLayer
    ? t("map_overlap_current_code_areas")
    : t("map_overlap_current_neighbors");
  const neighborNeighborOverlapLabel = hasServiceAreaLayer
    ? t("map_overlap_between_code_areas")
    : t("map_overlap_between_neighbors");

  const outsideServiceIssue = useMemo(() => {
    const currentRing = currentCoords?.[0];
    if (!currentRing || !serviceAreaRings.length) return null;
    return outsideContainingAreaFeature(
      currentRing,
      serviceAreaRings.map((serviceArea) => serviceArea.ring),
    );
  }, [currentCoords, serviceAreaRings]);

  const markMapKeyboardActive = useCallback(() => {
    mapKeyboardActiveRef.current = true;
  }, []);

  const handleMapLoad = useCallback((map: google.maps.Map) => {
    mapRef.current = map;
    mapPointerCleanupRef.current?.();
    const mapDiv = map.getDiv();
    mapDiv.addEventListener("pointerdown", markMapKeyboardActive);
    mapPointerCleanupRef.current = () => {
      mapDiv.removeEventListener("pointerdown", markMapKeyboardActive);
    };
    // The adapter attaches pointer listeners to an internal pane that only
    // exists after the first render; initialise Terra Draw on the first `idle`.
    const gmaps = (window as any).google?.maps;
    if (gmaps?.event?.addListenerOnce) {
      gmaps.event.addListenerOnce(map, "idle", () => setMapInstance(map));
    } else {
      setMapInstance(map);
    }
  }, [markMapKeyboardActive]);

  const handleMapUnmount = useCallback(() => {
    mapPointerCleanupRef.current?.();
    mapPointerCleanupRef.current = null;
    mapRef.current = null;
    mapKeyboardActiveRef.current = false;
    setMapInstance(null);
  }, []);

  // AntD popups must render inside the map container, or they are invisible in
  // fullscreen (document.body is outside the fullscreen element).
  const mapPopupContainer = useCallback(
    () => mapRef.current?.getDiv() ?? document.body,
    [],
  );

  const syncHistoryState = useCallback((draw = drawRef.current) => {
    const localCanUndo = historyIndexRef.current > 0;
    const localCanRedo =
      historyIndexRef.current >= 0 &&
      historyIndexRef.current < historyRef.current.length - 1;
    let drawCanUndo = false;
    let drawCanRedo = false;
    try {
      drawCanUndo = !!draw?.canUndo();
      drawCanRedo = !!draw?.canRedo();
    } catch {
      drawCanUndo = false;
      drawCanRedo = false;
    }
    setCanUndo(localCanUndo || drawCanUndo);
    setCanRedo(localCanRedo || drawCanRedo);
  }, []);

  const resetLocalHistory = useCallback(
    (coordinates: HistoryCoordinates) => {
      historyRef.current = [cloneHistoryCoordinates(coordinates)];
      historyIndexRef.current = 0;
      syncHistoryState();
    },
    [syncHistoryState],
  );

  const pushLocalHistory = useCallback(
    (coordinates: HistoryCoordinates) => {
      const snapshot = cloneHistoryCoordinates(coordinates);
      const current = historyRef.current[historyIndexRef.current] ?? null;
      if (historyCoordinatesKey(current) === historyCoordinatesKey(snapshot)) {
        syncHistoryState();
        return;
      }
      historyRef.current = historyRef.current.slice(
        0,
        historyIndexRef.current + 1,
      );
      historyRef.current.push(snapshot);
      historyIndexRef.current = historyRef.current.length - 1;
      syncHistoryState();
    },
    [syncHistoryState],
  );

  // The single area polygon the user is drawing/editing (mode === "polygon").
  const getUserFeature = useCallback((): any => {
    const draw = drawRef.current;
    if (!draw) return null;
    const snapshot = draw.getSnapshot();
    if (userFeatureIdRef.current != null) {
      const tracked = snapshot.find(
        (f: any) => f.id === userFeatureIdRef.current,
      );
      if (tracked) return tracked;
    }
    return (
      snapshot.find((f: any) => f?.properties?.mode === POLYGON_MODE) ?? null
    );
  }, []);

  const hasEditHandlesForFeature = useCallback((featureId: string | number) => {
    const draw = drawRef.current;
    if (!draw) return false;
    const id = String(featureId);
    try {
      return draw.getSnapshot().some((f: any) => {
        const props = f?.properties;
        return (
          String(props?.selectionPointFeatureId) === id ||
          String(props?.midPointFeatureId) === id
        );
      });
    } catch {
      return false;
    }
  }, []);

  // Lift the drawn polygon to the parent form. Called only on completed actions
  // (finish / undo / redo / fixes) — never mid-drag — so editing stays smooth.
  const commit = useCallback((options?: { recordHistory?: boolean }) => {
    const feature = getUserFeature();
    if (!feature || feature.geometry?.type !== "Polygon") {
      userFeatureIdRef.current = null;
      syncedRingKeyRef.current = null;
      setCurrentCoords(null);
      onChangeRef.current?.({ type: "Polygon", coordinates: [] });
      if (options?.recordHistory !== false) {
        pushLocalHistory(null);
      }
      return;
    }
    if (feature.id != null) {
      userFeatureIdRef.current = feature.id;
    }
    const ring = ensureClosed(
      (feature.geometry.coordinates[0] as number[][]).map((c) => [
        round6(c[0]),
        round6(c[1]),
      ]),
    );
    const coordinates = [ring];
    setCurrentCoords(coordinates);
    // Remember what we emit so the prop echo is recognised as already-in-sync.
    syncedRingKeyRef.current = ringKey(ring);
    onChangeRef.current?.({ type: "Polygon", coordinates });
    if (options?.recordHistory !== false) {
      pushLocalHistory(coordinates);
    }
  }, [getUserFeature, pushLocalHistory]);

  const selectUserFeatureForEdit = useCallback(
    (draw = drawRef.current, options?: { defer?: boolean }) => {
      if (!draw) return false;
      const feature = getUserFeature();
      if (!feature?.id) return false;
      userFeatureIdRef.current = feature.id;

      const activate = (attempt = 0) => {
        const latestFeature = getUserFeature();
        if (!latestFeature?.id || latestFeature.id !== feature.id) {
          if (options?.defer && attempt < 24 && typeof window !== "undefined") {
            window.requestAnimationFrame(() => activate(attempt + 1));
          }
          return;
        }
        if (hasEditHandlesForFeature(feature.id)) {
          return;
        }
        try {
          draw.setMode(SELECT_MODE);
        } catch {
          /* noop */
        }
        try {
          draw.deselectFeature(feature.id);
        } catch {
          /* noop */
        }
        let selected = false;
        try {
          draw.selectFeature(feature.id);
          selected = true;
        } catch {
          /* noop */
        }
        const handlesReady = hasEditHandlesForFeature(feature.id);
        if (
          (!selected || !handlesReady) &&
          options?.defer &&
          attempt < 24 &&
          typeof window !== "undefined"
        ) {
          window.requestAnimationFrame(() => activate(attempt + 1));
        }
      };

      if (options?.defer && typeof window !== "undefined") {
        window.requestAnimationFrame(() => activate(0));
      } else {
        activate();
      }

      setActiveTool("edit");
      return true;
    },
    [getUserFeature, hasEditHandlesForFeature],
  );

  const applyHistoryCoordinates = useCallback(
    (coordinates: HistoryCoordinates) => {
      const draw = drawRef.current;
      if (!draw) return;
      const snapshot = cloneHistoryCoordinates(coordinates);

      suppressRef.current = true;
      try {
        if (!snapshot) {
          const stale = draw
            .getSnapshot()
            .filter((f: any) => f?.properties?.mode === POLYGON_MODE)
            .map((f: any) => f.id);
          if (stale.length) draw.removeFeatures(stale);
          userFeatureIdRef.current = null;
          syncedRingKeyRef.current = null;
          setCurrentCoords(null);
          setMoveAreaEnabled(false);
          onChangeRef.current?.({ type: "Polygon", coordinates: [] });
          try {
            draw.setMode(POLYGON_MODE);
          } catch {
            /* noop */
          }
          setActiveTool("draw");
          return;
        }

        const feature = getUserFeature();
        if (feature?.id != null) {
          draw.updateFeatureGeometry(feature.id, {
            type: "Polygon",
            coordinates: snapshot,
          });
          userFeatureIdRef.current = feature.id;
        } else {
          const result = draw.addFeatures([
            {
              type: "Feature",
              properties: { mode: POLYGON_MODE },
              geometry: { type: "Polygon", coordinates: snapshot },
            } as any,
          ]);
          const id = result?.[0]?.id;
          if (id != null) userFeatureIdRef.current = id;
        }

        setCurrentCoords(snapshot);
        syncedRingKeyRef.current = ringKey(snapshot[0]);
        onChangeRef.current?.({ type: "Polygon", coordinates: snapshot });
        selectUserFeatureForEdit(draw, { defer: true });
      } catch {
        /* noop */
      } finally {
        suppressRef.current = false;
        try {
          draw.clearUndoRedoHistory();
        } catch {
          /* noop */
        }
        syncHistoryState(draw);
      }
    },
    [getUserFeature, selectUserFeatureForEdit, syncHistoryState],
  );

  // Snap onto the closest neighbouring border (edge or corner) within
  // SNAP_PIXEL_DISTANCE. Runs last in Terra Draw's snapping chain, so it wins
  // over self-snapping when a neighbour is nearby, and falls back otherwise.
  const snapToNeighbors = useCallback((event: any, context: any) => {
    if (!snapEnabledRef.current) return undefined;
    const draw = drawRef.current;
    if (!draw) return undefined;
    const project = context?.project;
    const unproject = context?.unproject;
    if (!project || !unproject) return undefined;

    const cx = event.containerX;
    const cy = event.containerY;
    let best: { x: number; y: number } | null = null;
    let bestDist = SNAP_PIXEL_DISTANCE;

    for (const neighbor of snapReferenceRingsRef.current) {
      const ring = neighbor.ring;
      for (let i = 0; i < ring.length - 1; i++) {
        const a = project(ring[i][0], ring[i][1]);
        const b = project(ring[i + 1][0], ring[i + 1][1]);
        const p = nearestOnSegment(cx, cy, a.x, a.y, b.x, b.y);
        const d = Math.hypot(p.x - cx, p.y - cy);
        if (d < bestDist) {
          bestDist = d;
          best = p;
        }
      }
    }

    if (!best) return undefined;
    const ll = unproject(best.x, best.y);
    return [ll.lng, ll.lat];
  }, []);

  const createSnappingOptions = useCallback(
    (enabled = snapEnabledRef.current) =>
      enabled
        ? {
            toLine: true,
            toCoordinate: true,
            toCustom: snapToNeighbors,
          }
        : {
            toLine: false,
            toCoordinate: false,
          },
    [snapToNeighbors],
  );

  const createSelectFlags = useCallback(
    (
      snap = snapEnabledRef.current,
      moveArea = moveAreaEnabledRef.current,
    ) => ({
      polygon: {
        feature: {
          draggable: moveArea,
          // Keep editing unlocked even while the polygon is currently invalid.
          // Validation still warns/blocks saving below; this lets the user fix it.
          selfIntersectable: true,
          coordinates: {
            snappable: createSnappingOptions(snap),
            midpoints: true,
            draggable: true,
            deletable: true,
          },
        },
      },
    }),
    [createSnappingOptions],
  );

  const syncDrawInteractionOptions = useCallback(
    (draw = drawRef.current) => {
      if (!draw) return;
      try {
        draw.updateModeOptions(POLYGON_MODE, {
          snapping: createSnappingOptions(),
        } as any);
      } catch {
        /* noop */
      }
      try {
        draw.updateModeOptions(SELECT_MODE, {
          flags: createSelectFlags(),
        } as any);
      } catch {
        /* noop */
      }
    },
    [createSelectFlags, createSnappingOptions],
  );

  useEffect(() => {
    snapEnabledRef.current = snapEnabled;
    syncDrawInteractionOptions();
  }, [snapEnabled, syncDrawInteractionOptions]);

  useEffect(() => {
    moveAreaEnabledRef.current = moveAreaEnabled;
    syncDrawInteractionOptions();
  }, [moveAreaEnabled, syncDrawInteractionOptions]);

  // Load the polygon coming from the form into the store, but only for genuinely
  // external values (initial load / form reset), never for our own edit echoes.
  const syncUserFromProps = useCallback(() => {
    const draw = drawRef.current;
    if (!draw) return;
    if (!drawReadyRef.current) return;

    const incomingRing = pointsRef.current?.coordinates?.[0] ?? null;
    const incomingKey = ringKey(incomingRing);
    if (incomingKey === syncedRingKeyRef.current) {
      return;
    }

    suppressRef.current = true;
    try {
      const stale = draw
        .getSnapshot()
        .filter((f: any) => f?.properties?.mode === POLYGON_MODE)
        .map((f: any) => f.id);
      if (stale.length) draw.removeFeatures(stale);
      userFeatureIdRef.current = null;
      syncedRingKeyRef.current = incomingKey;

      if (Array.isArray(incomingRing) && incomingRing.length >= 3) {
        const closed = ensureClosed(incomingRing);
        const result = draw.addFeatures([
          {
            type: "Feature",
            properties: { mode: POLYGON_MODE },
            geometry: { type: "Polygon", coordinates: [closed] },
          } as any,
        ]);
        const id = result?.[0]?.id;
        if (id != null) userFeatureIdRef.current = id;
        const coordinates = cloneHistoryCoordinates([closed]);
        setCurrentCoords(coordinates);
        resetLocalHistory(coordinates);

        if (!didCenterRef.current && mapRef.current && (window as any).google) {
          try {
            const bounds = new (window as any).google.maps.LatLngBounds();
            closed.forEach((c) => bounds.extend({ lat: c[1], lng: c[0] }));
            mapRef.current.fitBounds(bounds);
            didCenterRef.current = true;
          } catch {
            /* noop */
          }
        }

        if (interactive) {
          selectUserFeatureForEdit(draw, { defer: true });
        }
      } else {
        setCurrentCoords(null);
        resetLocalHistory(null);
      }

      // A genuine external (re)load is not part of the user's edit history.
      try {
        draw.clearUndoRedoHistory();
      } catch {
        /* noop */
      }
      syncHistoryState(draw);
    } catch {
      /* noop */
    } finally {
      suppressRef.current = false;
    }
  }, [
    interactive,
    resetLocalHistory,
    selectUserFeatureForEdit,
    syncHistoryState,
  ]);

  // Build the Terra Draw instance once the Google map is ready.
  useEffect(() => {
    if (!isLoaded || !mapInstance || !(window as any).google) return;

    const snapping = createSnappingOptions();

    const draw = new TerraDraw({
      adapter: new TerraDrawGoogleMapsAdapter({
        lib: (window as any).google.maps,
        map: mapInstance,
        isolatedData: true,
        coordinatePrecision: 9,
      }),
      // Keep Terra Draw history only for an in-progress drawing. Completed
      // polygon edits use local coordinate history below because Terra Draw's
      // session replay removes and re-adds the same Google Data feature in one
      // frame, which can leave only edit points visible without the polygon line.
      undoRedo: {
        modeLevel: new TerraDrawModeUndoRedo(),
        keyboardShortcuts: new TerraDrawUndoRedoKeyboardShortcuts(),
      },
      modes: [
        new TerraDrawPolygonMode({
          snapping,
          editable: true,
          pointerDistance: 30,
          styles: {
            fillColor: VALID_COLOR,
            fillOpacity: 0.3,
            outlineColor: VALID_COLOR,
            outlineOpacity: 1,
            outlineWidth: 2,
            coordinatePointWidth: 5,
            coordinatePointColor: VALID_COLOR,
            coordinatePointOutlineColor: "#ffffff",
            coordinatePointOutlineWidth: 2,
            snappingPointColor: NEIGHBOR_COLOR,
            snappingPointWidth: 9,
            snappingPointOutlineColor: "#ffffff",
            snappingPointOutlineWidth: 3,
          },
        }),
        new TerraDrawSelectMode({
          allowManualSelection: true,
          // Keep the polygon selected so the edit handles never disappear on a
          // stray click — the user switches away via the Draw tool instead.
          allowManualDeselection: false,
          flags: {
            ...createSelectFlags(),
          },
          styles: {
            selectedPolygonColor: VALID_COLOR,
            selectedPolygonFillOpacity: 0.3,
            selectedPolygonOutlineColor: VALID_COLOR,
            selectedPolygonOutlineOpacity: 1,
            selectedPolygonOutlineWidth: 2,
            selectionPointColor: VALID_COLOR,
            selectionPointOpacity: 1,
            selectionPointWidth: 7,
            selectionPointOutlineColor: "#ffffff",
            selectionPointOutlineOpacity: 1,
            selectionPointOutlineWidth: 2,
            midPointColor: MIDPOINT_COLOR,
            midPointOpacity: 1,
            midPointWidth: 4,
            midPointOutlineColor: "#ffffff",
            midPointOutlineOpacity: 1,
            midPointOutlineWidth: 1,
          },
        }),
      ],
    });

    drawReadyRef.current = false;
    drawRef.current = draw;

    // Geometry is lifted to the form ONLY on completed actions. (Mid-drag
    // `change` events are intentionally ignored so the parent never re-renders
    // during a drag — that was what made editing feel laggy/broken.)
    const handleFinish = (id: any, ctx: any) => {
      if (ctx?.action === "draw") {
        // Keep a single area: drop any older polygon, keep the just-drawn one.
        const others = draw
          .getSnapshot()
          .filter(
            (f: any) => f?.properties?.mode === POLYGON_MODE && f.id !== id,
          )
          .map((f: any) => f.id);
        if (others.length) {
          suppressRef.current = true;
          try {
            draw.removeFeatures(others);
          } catch {
            /* noop */
          } finally {
            suppressRef.current = false;
          }
        }
        userFeatureIdRef.current = id;
        // Hand straight over to editing the fresh shape.
        selectUserFeatureForEdit(draw, { defer: true });
      }
      commit();
      try {
        draw.clearUndoRedoHistory();
      } catch {
        /* noop */
      }
      syncHistoryState(draw);
    };

    // Mode-level history only applies while a polygon is still being drawn.
    // Completed polygon edits are restored from the local coordinate history.
    const handleHistory = () => {
      syncHistoryState(draw);
    };

    let didInitialise = false;
    const initialiseDrawState = () => {
      if (didInitialise) return;
      didInitialise = true;
      drawReadyRef.current = true;
      syncDrawInteractionOptions(draw);
      syncUserFromProps();
      if (interactive) {
        if (!getUserFeature()) {
          try {
            draw.setMode(POLYGON_MODE);
          } catch {
            /* noop */
          }
          setActiveTool("draw");
        } else {
          selectUserFeatureForEdit(draw, { defer: true });
        }
      } else {
        try {
          draw.setMode("static");
        } catch {
          /* noop */
        }
      }
      syncHistoryState(draw);
    };

    draw.on("finish", handleFinish);
    draw.on("history", handleHistory);
    draw.on("ready", initialiseDrawState);

    draw.start();
    const initialiseTimer = window.setTimeout(initialiseDrawState, 120);

    return () => {
      window.clearTimeout(initialiseTimer);
      try {
        draw.off("finish", handleFinish);
      } catch {
        /* noop */
      }
      try {
        draw.off("history", handleHistory);
      } catch {
        /* noop */
      }
      try {
        draw.off("ready", initialiseDrawState);
      } catch {
        /* noop */
      }
      try {
        draw.stop();
      } catch {
        /* noop */
      }
      drawRef.current = null;
      userFeatureIdRef.current = null;
      drawReadyRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded, mapInstance, interactive]);

  // Keep the store in sync with external form/props changes after init.
  useEffect(() => {
    if (drawRef.current) syncUserFromProps();
  }, [syncUserFromProps]);

  const handleSelectDrawTool = useCallback(() => {
    const draw = drawRef.current;
    if (!draw) return;
    setMoveAreaEnabled(false);
    try {
      draw.setMode(POLYGON_MODE);
    } catch {
      /* noop */
    }
    setActiveTool("draw");
  }, []);

  const handleSelectEditTool = useCallback(() => {
    const draw = drawRef.current;
    if (!draw) return;
    if (!selectUserFeatureForEdit(draw)) {
      try {
        draw.setMode(POLYGON_MODE);
      } catch {
        /* noop */
      }
      setActiveTool("draw");
    }
  }, [selectUserFeatureForEdit]);

  const handleToggleMoveArea = useCallback(
    (enabled: boolean) => {
      const draw = drawRef.current;
      setMoveAreaEnabled(enabled);
      if (enabled && draw) {
        selectUserFeatureForEdit(draw, { defer: true });
      }
    },
    [selectUserFeatureForEdit],
  );

  const handleClearAll = useCallback(() => {
    const draw = drawRef.current;
    if (!draw) return;
    suppressRef.current = true;
    try {
      const stale = draw
        .getSnapshot()
        .filter((f: any) => f?.properties?.mode === POLYGON_MODE)
        .map((f: any) => f.id);
      if (stale.length) draw.removeFeatures(stale);
    } catch {
      /* noop */
    } finally {
      suppressRef.current = false;
    }
    userFeatureIdRef.current = null;
    syncedRingKeyRef.current = null;
    setCurrentCoords(null);
    setMoveAreaEnabled(false);
    onChangeRef.current?.({ type: "Polygon", coordinates: [] });
    pushLocalHistory(null);
    try {
      draw.clearUndoRedoHistory();
    } catch {
      /* noop */
    }
    syncHistoryState(draw);
    try {
      draw.setMode(POLYGON_MODE);
    } catch {
      /* noop */
    }
    setActiveTool("draw");
  }, [pushLocalHistory, syncHistoryState]);

  const handleUndo = useCallback(() => {
    const draw = drawRef.current;
    if (!draw) return;

    try {
      if (draw.getModeState() === "drawing" && draw.canUndo()) {
        draw.undo();
        syncHistoryState(draw);
        return;
      }
    } catch {
      /* noop */
    }

    if (historyIndexRef.current > 0) {
      historyIndexRef.current -= 1;
      applyHistoryCoordinates(historyRef.current[historyIndexRef.current]);
      return;
    }

    try {
      if (!draw.undo()) syncHistoryState(draw);
    } catch {
      syncHistoryState(draw);
    }
  }, [applyHistoryCoordinates, syncHistoryState]);

  const handleRedo = useCallback(() => {
    const draw = drawRef.current;
    if (!draw) return;

    try {
      if (draw.getModeState() === "drawing" && draw.canRedo()) {
        draw.redo();
        syncHistoryState(draw);
        return;
      }
    } catch {
      /* noop */
    }

    if (historyIndexRef.current < historyRef.current.length - 1) {
      historyIndexRef.current += 1;
      applyHistoryCoordinates(historyRef.current[historyIndexRef.current]);
      return;
    }

    try {
      if (!draw.redo()) syncHistoryState(draw);
    } catch {
      syncHistoryState(draw);
    }
  }, [applyHistoryCoordinates, syncHistoryState]);

  useEffect(() => {
    if (!interactive) return;

    const handleKeyboardHistory = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      const isEditableTarget =
        target?.isContentEditable ||
        target?.tagName === "INPUT" ||
        target?.tagName === "TEXTAREA" ||
        target?.tagName === "SELECT";
      if (isEditableTarget) return;
      if (!mapKeyboardActiveRef.current) return;

      const key = event.key.toLowerCase();
      const isModifier = event.ctrlKey || event.metaKey;
      const isUndo = isModifier && key === "z" && !event.shiftKey;
      const isRedo =
        isModifier && (key === "y" || (key === "z" && event.shiftKey));
      if (!isUndo && !isRedo) return;

      event.preventDefault();
      if (isRedo) {
        handleRedo();
      } else {
        handleUndo();
      }
    };

    window.addEventListener("keydown", handleKeyboardHistory);
    return () => {
      window.removeEventListener("keydown", handleKeyboardHistory);
    };
  }, [handleRedo, handleUndo, interactive]);

  // Zoom/pan the map to frame the current area.
  const handleFit = useCallback(() => {
    const map = mapRef.current;
    const ring = currentCoords?.[0];
    const gmaps = (window as any).google?.maps;
    if (!map || !gmaps || !ring?.length) return;
    try {
      const bounds = new gmaps.LatLngBounds();
      ring.forEach((c) => bounds.extend({ lat: c[1], lng: c[0] }));
      map.fitBounds(bounds);
    } catch {
      /* noop */
    }
  }, [currentCoords]);

  // Replace the polygon geometry with a repaired ring (used by the fix buttons).
  const applyRepairedRing = useCallback(
    (openRing: number[][]) => {
      const draw = drawRef.current;
      const feature = getUserFeature();
      if (!draw || feature?.id == null || openRing.length < 3) return;
      const closed = ensureClosed(
        openRing.map((c) => [round6(c[0]), round6(c[1])]),
      );
      try {
        draw.updateFeatureGeometry(feature.id, {
          type: "Polygon",
          coordinates: [closed],
        });
      } catch {
        /* noop */
      }
      try {
        draw.selectFeature(feature.id);
      } catch {
        /* noop */
      }
      commit();
      try {
        draw.clearUndoRedoHistory();
      } catch {
        /* noop */
      }
      syncHistoryState(draw);
    },
    [getUserFeature, commit, syncHistoryState],
  );

  const validation = useMemo(
    () => validatePolygonCoordinates(currentCoords ?? undefined),
    [currentCoords],
  );

  // Live measurements shown below the map: corner count + perimeter.
  const polygonMetrics = useMemo(() => {
    const ring = currentCoords?.[0];
    if (!ring || ring.length < 4) return { vertices: 0, perimeterKm: 0 };
    const latlngs = ring.map((c) => ({ lat: c[1], lng: c[0] }));
    return {
      vertices: ring.length - 1, // exclude the closing point
      perimeterKm: calculateRouteDistance(latlngs),
    };
  }, [currentCoords]);

  const issues = useMemo(
    () => detectPolygonIssues(currentCoords?.[0] ?? null),
    [currentCoords],
  );

  // Red highlight points: the crossing point for self-intersections, the
  // offending vertices for duplicates/spikes.
  const issuePoints = useMemo(() => {
    const pts: { lat: number; lng: number; key: string }[] = [];
    issues.forEach((iss, idx) => {
      if (iss.type === "self_intersection") {
        const c = iss.coordinates[0];
        if (c) pts.push({ lat: c[1], lng: c[0], key: `x-${idx}` });
      } else if (iss.type === "duplicate" || iss.type === "spike") {
        iss.coordinates.forEach((c, j) =>
          pts.push({ lat: c[1], lng: c[0], key: `p-${idx}-${j}` }),
        );
      }
    });
    return pts;
  }, [issues]);

  const fixableIssues = useMemo(
    () => issues.filter((i) => !!i.fix),
    [issues],
  );

  // Collapse the issues into one row per problem type, with a count.
  const issueGroups = useMemo(() => {
    const acc: Record<
      string,
      { type: PolygonIssue["type"]; count: number; fixable: boolean }
    > = {};
    issues.forEach((iss) => {
      if (!acc[iss.type]) {
        acc[iss.type] = { type: iss.type, count: 0, fixable: !!iss.fix };
      }
      acc[iss.type].count +=
        iss.type === "duplicate" ? iss.vertexIndices.length : 1;
    });
    return ISSUE_TYPE_ORDER.filter((tpe) => acc[tpe]).map((tpe) => acc[tpe]);
  }, [issues]);

  const totalIssueCount = useMemo(
    () => issueGroups.reduce((s, g) => s + g.count, 0),
    [issueGroups],
  );

  useEffect(() => {
    onGeometryIssuesChangeRef.current?.({
      outsideServiceArea: outsideServiceIssue?.area ?? 0,
      currentNeighborOverlapArea: totalCurrentNeighborOverlapArea,
      neighborNeighborOverlapArea: totalNeighborNeighborOverlapArea,
      polygonIssueCount: totalIssueCount,
    });
  }, [
    outsideServiceIssue,
    totalCurrentNeighborOverlapArea,
    totalNeighborNeighborOverlapArea,
    totalIssueCount,
  ]);

  // Fix every problem of a single type at once (grouped "Fix" buttons).
  const fixType = useCallback(
    (type: PolygonIssue["type"]) => {
      const ring = currentCoords?.[0];
      if (!ring) return;
      if (type === "duplicate") {
        applyRepairedRing(removeDuplicatePoints(ring));
      } else if (type === "self_intersection") {
        applyRepairedRing(autoRepairSelfIntersection(ring));
      } else if (type === "spike") {
        let r = ring;
        findSpikes(ring)
          .sort((a, b) => b - a)
          .forEach((i) => {
            r = removeVertex(r, i);
          });
        applyRepairedRing(r);
      }
    },
    [currentCoords, applyRepairedRing],
  );

  const handleAutoFixAll = useCallback(() => {
    const ring = currentCoords?.[0];
    if (!ring) return;
    let r = removeDuplicatePoints(ring);
    r = autoRepairSelfIntersection(r);
    findSpikes(r)
      .sort((a, b) => b - a)
      .forEach((i) => {
        r = removeVertex(r, i);
      });
    applyRepairedRing(r);
  }, [currentCoords, applyRepairedRing]);

  const handleTrimCurrentOverlaps = useCallback(() => {
    const ring = currentCoords?.[0];
    if (!ring) return;
    const repaired = subtractNeighborOverlaps(
      ring,
      neighborRings.map((neighbor) => neighbor.ring),
    );
    if (repaired) {
      applyRepairedRing(repaired);
    }
  }, [currentCoords, neighborRings, applyRepairedRing]);

  const handleTrimOutsideServiceAreas = useCallback(() => {
    const ring = currentCoords?.[0];
    if (!ring) return;
    const repaired = trimRingToContainingAreas(
      ring,
      serviceAreaRings.map((serviceArea) => serviceArea.ring),
    );
    if (repaired) {
      applyRepairedRing(repaired);
    }
  }, [currentCoords, serviceAreaRings, applyRepairedRing]);

  const handleMarkerClick = (marker: IResponseTracking | IStore) => {
    if ("user_data" in marker) {
      setSelectedMarker(marker as IResponseTracking);
      setSelectedStoreMarker(null);
    } else {
      setSelectedStoreMarker(marker as IStore);
      setSelectedMarker(null);
    }
  };

  const hasPolygon = !!currentCoords;

  // The editing toolbar — rendered into a map control so it is also visible in
  // fullscreen. (Compact: tools live here; detailed metrics/fixes are below.)
  const toolbar = (
    <div className={styles.mapToolbar}>
      <Segmented
        className={styles.mapToolbarSegmented}
        size="small"
        value={activeTool}
        onChange={(value) =>
          value === "draw" ? handleSelectDrawTool() : handleSelectEditTool()
        }
        options={[
          {
            label: hasPolygon ? t("map_tool_redraw") : t("map_tool_draw"),
            value: "draw",
            icon: <BorderOutlined />,
          },
          {
            label: t("map_tool_edit"),
            value: "edit",
            icon: <EditOutlined />,
            disabled: !hasPolygon,
          },
        ]}
      />

      <Divider type="vertical" className={styles.mapToolbarDivider} />

      <Tooltip title={`${t("undo")} (Ctrl+Z)`} getPopupContainer={mapPopupContainer}>
        <Button
          size="small"
          aria-label={t("undo")}
          icon={<UndoOutlined />}
          onClick={handleUndo}
          disabled={!canUndo}
        />
      </Tooltip>
      <Tooltip
        title={`${t("redo")} (Ctrl+Shift+Z)`}
        getPopupContainer={mapPopupContainer}
      >
        <Button
          size="small"
          aria-label={t("redo")}
          icon={<RedoOutlined />}
          onClick={handleRedo}
          disabled={!canRedo}
        />
      </Tooltip>
      <Tooltip title={t("map_fit")} getPopupContainer={mapPopupContainer}>
        <Button
          size="small"
          aria-label={t("map_fit")}
          icon={<AimOutlined />}
          onClick={handleFit}
          disabled={!hasPolygon}
        />
      </Tooltip>
      <Tooltip
        title={t("map_move_area_hint")}
        getPopupContainer={mapPopupContainer}
      >
        <Button
          size="small"
          type={moveAreaEnabled ? "primary" : "default"}
          aria-label={t("map_tool_move_area")}
          aria-pressed={moveAreaEnabled}
          icon={<DragOutlined />}
          onClick={() => handleToggleMoveArea(!moveAreaEnabled)}
          disabled={!hasPolygon}
        />
      </Tooltip>

      <Divider type="vertical" className={styles.mapToolbarDivider} />

      <Tooltip title={t("map_snap_hint")} getPopupContainer={mapPopupContainer}>
        <span className={styles.mapToolbarSnap}>
          <NodeIndexOutlined />
          <span className={styles.mapToolbarSnapLabel}>{t("map_snap")}</span>
          <Switch
            className={styles.mapToolbarSwitch}
            size="small"
            checked={snapEnabled}
            onChange={setSnapEnabled}
          />
        </span>
      </Tooltip>

      <Divider type="vertical" className={styles.mapToolbarDivider} />

      <Popconfirm
        title={t("map_clear_confirm")}
        okText={t("map_tool_clear")}
        cancelText={t("cancel")}
        okButtonProps={{ danger: true }}
        onConfirm={handleClearAll}
        disabled={!hasPolygon}
        getPopupContainer={mapPopupContainer}
      >
        <Tooltip title={t("map_tool_clear")} getPopupContainer={mapPopupContainer}>
          <Button
            size="small"
            danger
            className={styles.mapToolbarDanger}
            aria-label={t("map_tool_clear")}
            icon={<DeleteOutlined />}
            disabled={!hasPolygon}
          />
        </Tooltip>
      </Popconfirm>

      {fixableIssues.length > 0 && (
        <>
          <Divider type="vertical" className={styles.mapToolbarDivider} />
          <Tooltip
            title={t("map_fix_all")}
            getPopupContainer={mapPopupContainer}
          >
            <Button
              size="small"
              type="primary"
              icon={<ThunderboltOutlined />}
              onClick={handleAutoFixAll}
            >
              {fixableIssues.length}
            </Button>
          </Tooltip>
        </>
      )}
    </div>
  );

  return (
    <div>
      {!isLoaded && (
        <div
          style={{
            ...containerStyle,
            width: mapWidth,
            height: mapHeight,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 12,
            background: "rgba(0, 0, 0, 0.02)",
            border: "1px solid rgba(0, 0, 0, 0.06)",
            borderRadius: 8,
          }}
        >
          <Spin size="large" />
          <span style={{ color: "#8c8c8c" }}>{t("map_loading")}</span>
        </div>
      )}
      {isLoaded && (
        <GoogleMap
          mapContainerStyle={{ ...containerStyle, width: mapWidth, height: mapHeight }}
          center={center}
          zoom={15}
          onLoad={handleMapLoad}
          onUnmount={handleMapUnmount}
          options={{
            // Normal wheel scroll passes through to the page; Ctrl/⌘ + wheel
            // (or the +/- buttons) zooms — so the map never traps page scroll.
            gestureHandling: "cooperative",
            mapTypeControl: true,
            fullscreenControl: true,
            zoomControl: true,
            streetViewControl: false,
            clickableIcons: false,
          }}
        >
          {mapInstance && interactive && (
            <MapControlPortal
              map={mapInstance}
              position={window.google.maps.ControlPosition.BOTTOM_CENTER}
            >
              {toolbar}
            </MapControlPortal>
          )}

          {serviceAreaRings.map((serviceArea) => (
            <GooglePolygon
              key={serviceArea.key}
              path={serviceArea.path}
              options={{
                clickable: false,
                fillColor: SERVICE_AREA_COLOR,
                fillOpacity: 0.05,
                strokeColor: SERVICE_AREA_COLOR,
                strokeOpacity: 0.55,
                strokeWeight: 1,
                zIndex: 2,
              }}
            />
          ))}

          {neighborRings.map((neighbor) => (
            <GooglePolygon
              key={neighbor.key}
              path={neighbor.path}
              options={{
                clickable: false,
                fillColor: NEIGHBOR_COLOR,
                fillOpacity: 0.08,
                strokeColor: NEIGHBOR_COLOR,
                strokeOpacity: 0.65,
                strokeWeight: 1,
                zIndex: 4,
              }}
            />
          ))}

          {currentNeighborOverlaps.flatMap((issue) =>
            issue.paths.map((path, index) => (
              <GooglePolygon
                key={`${issue.key}-path-${index}`}
                path={path}
                options={{
                  clickable: false,
                  fillColor: CURRENT_OVERLAP_COLOR,
                  fillOpacity: 0.08,
                  strokeColor: CURRENT_OVERLAP_COLOR,
                  strokeOpacity: 0.95,
                  strokeWeight: 3,
                  zIndex: 7,
                }}
              />
            )),
          )}

          {neighborNeighborOverlaps.flatMap((issue) =>
            issue.paths.map((path, index) => (
              <GooglePolygon
                key={`${issue.key}-path-${index}`}
                path={path}
                options={{
                  clickable: false,
                  fillColor: NEIGHBOR_OVERLAP_COLOR,
                  fillOpacity: 0.06,
                  strokeColor: NEIGHBOR_OVERLAP_COLOR,
                  strokeOpacity: 0.85,
                  strokeWeight: 2,
                  zIndex: 6,
                }}
              />
            )),
          )}

          {outsideServiceIssue?.paths.map((path, index) => (
            <GooglePolygon
              key={`outside-service-${index}`}
              path={path}
              options={{
                clickable: false,
                fillColor: OUTSIDE_SERVICE_COLOR,
                fillOpacity: 0.12,
                strokeColor: OUTSIDE_SERVICE_COLOR,
                strokeOpacity: 0.95,
                strokeWeight: 3,
                zIndex: 8,
              }}
            />
          ))}

          {interactive &&
            overlapMarkers.map((marker) => (
              <Marker
                key={marker.key}
                position={marker.position}
                clickable={false}
                zIndex={10000}
                icon={{
                  path: window.google.maps.SymbolPath.CIRCLE,
                  scale: marker.kind === "current_neighbor" ? 8 : 6,
                  fillColor:
                    marker.kind === "current_neighbor"
                      ? CURRENT_OVERLAP_COLOR
                      : NEIGHBOR_OVERLAP_COLOR,
                  fillOpacity: 1,
                  strokeColor: "#fff7ed",
                  strokeWeight: 2,
                }}
              />
            ))}

          {interactive &&
            issuePoints.map((p) => (
              <Marker
                key={p.key}
                position={{ lat: p.lat, lng: p.lng }}
                clickable={false}
                zIndex={9999}
                icon={{
                  path: window.google.maps.SymbolPath.CIRCLE,
                  scale: 7,
                  fillColor: INVALID_COLOR,
                  fillOpacity: 1,
                  strokeColor: "#ffffff",
                  strokeWeight: 2,
                }}
              />
            ))}

          {location?.map((mark, index) => (
            <Marker
              key={`driver-${index}`}
              position={{ lat: mark.latitude, lng: mark.longitude }}
              onClick={() => handleMarkerClick(mark)}
              label={{
                text: mark.user_data
                  ? `${mark.user_data.info_payload.firstName} ${mark.user_data.info_payload.lastName}`
                  : "Location",
                className: "marker-label",
              }}
              icon={{
                url: getImageByType(1),
                scaledSize: new window.google.maps.Size(40, 40),
              }}
            />
          ))}

          {stores?.map((store, index) => {
            const color = store?.operationStoreCategory?.color ?? "#FF0000";
            const isPromoted = store?.pharmacyPromotion;
            const markerColor = isPromoted ? "#000" : "#FFF";

            const svg = `
                                  <svg width="47" height="59" viewBox="0 0 47 59" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <g clip-path="url(#clip0_246_1330)">
                                    <mask id="mask0_246_1330" style="mask-type:luminance" maskUnits="userSpaceOnUse" x="0" y="0" width="47" height="59">
                                    <path d="M46.7926 0H0V59H46.7926V0Z" fill="white"/>
                                    </mask>
                                    <g mask="url(#mask0_246_1330)">
                                    <path d="M46.7955 23.3977C46.7955 32.1957 41.9328 39.8553 34.7499 43.8426L24.7817 58.269C24.6279 58.4943 24.4215 58.6786 24.1803 58.8059C23.9391 58.9333 23.6705 58.9999 23.3977 58.9999C23.125 58.9999 22.8564 58.9333 22.6152 58.8059C22.374 58.6786 22.1676 58.4943 22.0138 58.269L12.0456 43.8426C4.86273 39.8553 0 32.1957 0 23.3977C0 10.4767 10.4796 0 23.3977 0C36.3159 0 46.7955 10.4767 46.7955 23.3977Z" fill="${color}"/>
                                    <path d="M23.3981 38.5897C31.7885 38.5897 38.5902 31.788 38.5902 23.3976C38.5902 15.0073 31.7885 8.20557 23.3981 8.20557C15.0078 8.20557 8.20605 15.0073 8.20605 23.3976C8.20605 31.788 15.0078 38.5897 23.3981 38.5897Z" fill="${markerColor}"/>
                                    <path d="M27.8805 16.1284L22.4916 13.2289C21.9193 12.9237 21.2421 12.9237 20.6698 13.2289L15.2904 16.1284C14.8994 16.3478 14.6514 16.7675 14.6514 17.2253C14.6514 17.6926 14.8898 18.1123 15.2904 18.3222L20.6793 21.2216C20.9655 21.3742 21.2803 21.4506 21.5855 21.4506C21.8907 21.4506 22.215 21.3742 22.4916 21.2216L27.8805 18.3222C28.2715 18.1123 28.5195 17.6926 28.5195 17.2253C28.5195 16.7675 28.2715 16.3478 27.8805 16.1284Z" fill="${color}"/>
                                    <path d="M19.791 22.2324L14.7836 19.7335C14.3926 19.5332 13.9538 19.5618 13.5818 19.7811C13.2194 20.0101 13 20.4011 13 20.8303V25.5612C13 26.3814 13.4578 27.1158 14.1923 27.4878L19.1996 29.9868C19.3713 30.0726 19.5621 30.1203 19.7529 30.1203C19.9722 30.1203 20.2011 30.0535 20.4014 29.939C20.7639 29.7101 20.9832 29.319 20.9832 28.8899V24.1591C20.9737 23.3388 20.5159 22.6044 19.791 22.2324Z" fill="${color}"/>
                                    <path d="M30.169 20.8303V23.1767C29.7112 23.0431 29.2247 22.9859 28.7383 22.9859C27.4412 22.9859 26.1726 23.4342 25.1712 24.2353C23.7977 25.3132 23.0156 26.9441 23.0156 28.7086C23.0156 29.176 23.0728 29.6433 23.1968 30.0916C23.0537 30.0726 22.9107 30.0153 22.7771 29.9295C22.4147 29.7101 22.1953 29.319 22.1953 28.8899V24.1591C22.1953 23.3388 22.6531 22.6044 23.378 22.2324L28.3854 19.7335C28.7765 19.5332 29.2152 19.5618 29.5872 19.7811C29.9496 20.0101 30.169 20.4011 30.169 20.8303Z" fill="${color}"/>
                                    <path d="M32.0575 25.9898C31.2754 25.0265 30.0831 24.416 28.7383 24.416C27.7273 24.416 26.7926 24.769 26.0581 25.3603C25.0662 26.1424 24.4463 27.3537 24.4463 28.7081C24.4463 30.0529 25.0567 31.2452 26.02 32.0273C26.7544 32.6377 27.7082 33.0001 28.7383 33.0001C29.8256 33.0001 30.808 32.59 31.5711 31.9319C32.4581 31.1402 33.0303 29.9957 33.0303 28.7081C33.0303 27.678 32.6679 26.7242 32.0575 25.9898ZM29.7207 28.9751C29.7207 29.2231 29.5872 29.4616 29.3773 29.5856L28.0325 30.3868C27.9181 30.4535 27.7941 30.4917 27.6605 30.4917C27.4221 30.4917 27.1837 30.3677 27.0501 30.1483C26.8498 29.8049 26.9547 29.3662 27.2981 29.1659L28.2901 28.5745V27.3728C28.2901 26.9818 28.6143 26.6575 29.0054 26.6575C29.3965 26.6575 29.7207 26.9818 29.7207 27.3728V28.9751Z" fill="${color}"/>
                                    </g>
                                    </g>
                                    <defs>
                                    <clipPath id="clip0_246_1330">
                                    <rect width="47" height="59" fill="white"/>
                                    </clipPath>
                                    </defs>
                                    </svg>
                                  `;
            const encoded = encodeURIComponent(svg).replace(/'/g, "%27").replace(/"/g, "%22");
            return (
              <Marker
                key={`store-${index}`}
                position={{
                  lat: store.location.coordinates[1],
                  lng: store.location.coordinates[0],
                }}
                onClick={() => handleMarkerClick(store)}
                icon={{
                  url: `data:image/svg+xml;charset=UTF-8,${encoded}`,
                  scaledSize: new window.google.maps.Size(40, 40),
                }}
              />
            )
          })}

          {selectedMarker && (
            <InfoWindow
              position={{ lat: selectedMarker.latitude, lng: selectedMarker.longitude }}
              onCloseClick={() => setSelectedMarker(null)}
            >
              <div className={styles?.container || "info-window"}>
                <strong>{t("first_name")} : {selectedMarker.user_data.info_payload.firstName}</strong>
                <strong>{t("last_name")} : {selectedMarker.user_data.info_payload.lastName}</strong>
                <strong>{t("mobile")} : {selectedMarker.user_data.info_payload.mobile || selectedMarker.user_data.info_payload.phone}</strong>
                <strong>{t("phone")} : {selectedMarker.user_data.info_payload.phone}</strong>
                <strong>{t("email")} : {selectedMarker.user_data.info_payload.email}</strong>
              </div>
            </InfoWindow>
          )}

          {selectedStoreMarker && (
            <InfoWindow
              position={{
                lat: selectedStoreMarker.location.coordinates[1],
                lng: selectedStoreMarker.location.coordinates[0],
              }}
              onCloseClick={() => setSelectedStoreMarker(null)}
            >
              <div className={styles?.container || "info-window"}>
                <strong>{t("name")} : {selectedStoreMarker.name}</strong>
                <strong>{t("phone")} : {selectedStoreMarker.firstPhone}</strong>
                <strong>{t("start_working_time")} : {selectedStoreMarker.startWorkingTime}</strong>
                <strong>{t("end_working_time")} : {selectedStoreMarker.endWorkingTime}</strong>
                <strong>{t("work_status")} : {t(getStorWorkingStatus(selectedStoreMarker.workStatus))}</strong>
                <strong>{t("pharmacy_promotion")} : {selectedStoreMarker.pharmacyPromotion ? t("yes") : t("no")}</strong>
              </div>
            </InfoWindow>
          )}
        </GoogleMap>
      )}

      {isLoaded && interactive && (
        <div
          style={{
            marginTop: 8,
            display: "flex",
            flexDirection: "column",
            gap: 8,
            padding: "8px 12px",
            border: "1px solid rgba(0, 0, 0, 0.06)",
            borderRadius: 8,
            background: "rgba(0, 0, 0, 0.02)",
          }}
        >
          {/* Live measurements */}
          <Space split={<Divider type="vertical" />} size={4} wrap>
            <span>
              <span style={{ color: "#8c8c8c" }}>{t("polygon_area")} </span>
              <strong>{formatPolygonArea(validation.area)}</strong>
            </span>
            {hasPolygon && (
              <span>
                <span style={{ color: "#8c8c8c" }}>{t("map_perimeter")} </span>
                <strong>{formatDistance(polygonMetrics.perimeterKm)}</strong>
              </span>
            )}
            {hasPolygon && (
              <span>
                <span style={{ color: "#8c8c8c" }}>{t("map_vertices")} </span>
                <strong>{polygonMetrics.vertices}</strong>
              </span>
            )}
          </Space>

          <Space size={[14, 4]} wrap>
            <span className={styles.mapLegendItem}>
              <span
                className={styles.mapLegendSwatch}
                style={{ background: VALID_COLOR }}
              />
              {t("map_current_area")}
            </span>
            <span className={styles.mapLegendItem}>
              <span
                className={styles.mapLegendSwatch}
                style={{ background: NEIGHBOR_COLOR }}
              />
              {neighborAreasLabel} ({neighborRings.length})
            </span>
            {serviceAreaRings.length > 0 && (
              <span className={styles.mapLegendItem}>
                <span
                  className={styles.mapLegendSwatch}
                  style={{ background: SERVICE_AREA_COLOR }}
                />
                {t("map_service_areas")} ({serviceAreaRings.length})
              </span>
            )}
            <span className={styles.mapLegendItem}>
              <span
                className={styles.mapLegendSwatch}
                style={{ background: CURRENT_OVERLAP_COLOR }}
              />
              {t("map_overlap_marks")}
            </span>
            {outsideServiceIssue && (
              <span className={styles.mapLegendItem}>
                <span
                  className={styles.mapLegendSwatch}
                  style={{ background: OUTSIDE_SERVICE_COLOR }}
                />
                {t("map_outside_service_marks")}
              </span>
            )}
          </Space>

          {outsideServiceIssue && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 6,
                padding: "8px 10px",
                borderRadius: 8,
                background: "rgba(168, 7, 26, 0.06)",
                border: "1px solid rgba(168, 7, 26, 0.25)",
              }}
            >
              <strong style={{ color: OUTSIDE_SERVICE_COLOR }}>
                <ExclamationCircleOutlined style={{ marginInlineEnd: 6 }} />
                {t("map_outside_service_title")}
              </strong>
              <Space size={[14, 4]} wrap>
                <span>
                  <span style={{ color: "#8c8c8c" }}>
                    {t("map_outside_service_area")}:
                  </span>{" "}
                  <strong style={{ color: OUTSIDE_SERVICE_COLOR }}>
                    {formatPolygonArea(outsideServiceIssue.area)}
                  </strong>
                </span>
                <Tooltip
                  title={t("map_trim_outside_service_hint")}
                  getPopupContainer={mapPopupContainer}
                >
                  <Button
                    size="small"
                    type="primary"
                    danger
                    icon={<ThunderboltOutlined />}
                    onClick={handleTrimOutsideServiceAreas}
                  >
                    {t("map_trim_outside_service")}
                  </Button>
                </Tooltip>
              </Space>
            </div>
          )}

          {totalOverlapCount > 0 && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 6,
                padding: "8px 10px",
                borderRadius: 8,
                background: "rgba(207, 19, 34, 0.06)",
                border: "1px solid rgba(207, 19, 34, 0.25)",
              }}
            >
              <strong style={{ color: CURRENT_OVERLAP_COLOR }}>
                <ExclamationCircleOutlined style={{ marginInlineEnd: 6 }} />
                {t("map_overlap_title")} ({totalOverlapCount})
              </strong>
              <Space size={[14, 4]} wrap>
                <span>
                  <span style={{ color: "#8c8c8c" }}>
                    {currentNeighborOverlapLabel}:
                  </span>{" "}
                  <strong style={{ color: CURRENT_OVERLAP_COLOR }}>
                    {formatPolygonArea(totalCurrentNeighborOverlapArea)}
                  </strong>
                </span>
                <span>
                  <span style={{ color: "#8c8c8c" }}>
                    {neighborNeighborOverlapLabel}:
                  </span>{" "}
                  <strong style={{ color: NEIGHBOR_OVERLAP_COLOR }}>
                    {formatPolygonArea(totalNeighborNeighborOverlapArea)}
                  </strong>
                </span>
              </Space>
              {currentNeighborOverlaps.length > 0 && (
                <Space size={8} wrap>
                  <Tooltip
                    title={t("map_trim_current_overlap_hint")}
                    getPopupContainer={mapPopupContainer}
                  >
                    <Button
                      size="small"
                      type="primary"
                      icon={<ThunderboltOutlined />}
                      onClick={handleTrimCurrentOverlaps}
                    >
                      {t("map_trim_current_overlap")}
                    </Button>
                  </Tooltip>
                </Space>
              )}
            </div>
          )}

          {/* Detected problems — grouped by type (red = problem, blue = fix),
              light theme. The red dots on the map pinpoint each location. */}
          {issues.length > 0 && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 6,
                padding: "8px 10px",
                borderRadius: 8,
                background: "rgba(212, 56, 13, 0.06)",
                border: "1px solid rgba(212, 56, 13, 0.25)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 8,
                  flexWrap: "wrap",
                }}
              >
                <strong style={{ color: INVALID_COLOR }}>
                  <ExclamationCircleOutlined style={{ marginInlineEnd: 6 }} />
                  {t("map_issues_title")} ({totalIssueCount})
                </strong>
              </div>
              <Space size={[14, 4]} wrap>
                {issueGroups.map((g) => (
                  <span
                    key={g.type}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 4,
                    }}
                  >
                    <span style={{ color: "#8c8c8c" }}>
                      {t(ISSUE_LABEL_KEYS[g.type] ?? g.type)}:
                    </span>
                    <strong style={{ color: INVALID_COLOR }}>{g.count}</strong>
                    {g.fixable && (
                      <Button
                        size="small"
                        type="link"
                        onClick={() => fixType(g.type)}
                        style={{ padding: "0 4px", height: "auto" }}
                      >
                        {t("map_fix")}
                      </Button>
                    )}
                  </span>
                ))}
              </Space>
            </div>
          )}

          {/* Gesture help */}
          <span style={{ color: "#8c8c8c" }}>
            {activeTool === "draw"
              ? hasPolygon
                ? snapEnabled
                  ? t("map_redraw_hint")
                  : t("map_redraw_hint_no_snap")
                : snapEnabled
                  ? t("map_draw_hint")
                  : t("map_draw_hint_no_snap")
              : snapEnabled
                ? moveAreaEnabled
                  ? t("map_edit_move_hint")
                  : t("map_edit_hint")
                : moveAreaEnabled
                  ? t("map_edit_move_hint_no_snap")
                  : t("map_edit_hint_no_snap")}
          </span>
        </div>
      )}
    </div>
  );
};

export default CombinedMap;
